import React, { useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button } from '@mui/material';

const COLORS = ['#06C755', '#1877F2', '#0084FF', '#E1306C', '#3664D1', '#FF6B35'];
const PARTICLE_COUNT = 85;
const LINE_DIST = 150;

const CHANNEL_COLORS = {
  line: '#06C755',
  fbPosts: '#1877F2',
  messenger: '#0084FF',
  instagram: '#E1306C',
  webChat: '#3664D1',
  phone: '#FF6B35',
};

const BADGE_POSITIONS = [
  { className: 'fb-pos-1', bgColor: '#06C755' },
  { className: 'fb-pos-2', bgColor: '#1877F2' },
  { className: 'fb-pos-3', bgColor: '#3664D1' },
  { className: 'fb-pos-4', bgColor: '#0084FF' },
  { className: 'fb-pos-5', bgColor: '#E1306C' },
  { className: 'fb-pos-6', bgColor: '#FF6B35' },
];

export const OmniHero = ({ onCtaClick }) => {
  const { t } = useTranslation('landing');
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animFrameRef = useRef(null);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const createParticles = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      particlesRef.current = [];
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const isAnchor = i < 6;
        particlesRef.current.push({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * (isAnchor ? 0.3 : 0.6),
          vy: (Math.random() - 0.5) * (isAnchor ? 0.3 : 0.6),
          r: isAnchor ? 5 : (1.5 + Math.random() * 2),
          color: isAnchor ? COLORS[i] : COLORS[Math.floor(Math.random() * COLORS.length)],
          alpha: isAnchor ? 0.7 : (0.15 + Math.random() * 0.25),
          isAnchor,
        });
      }
    };

    const draw = () => {
      const cw = canvas.offsetWidth;
      const ch = canvas.offsetHeight;
      ctx.clearRect(0, 0, cw, ch);

      const ps = particlesRef.current;
      for (let i = 0; i < ps.length; i++) {
        for (let j = i + 1; j < ps.length; j++) {
          const dx = ps[i].x - ps[j].x;
          const dy = ps[i].y - ps[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < LINE_DIST) {
            const a = (1 - dist / LINE_DIST) * 0.12;
            ctx.beginPath();
            ctx.moveTo(ps[i].x, ps[i].y);
            ctx.lineTo(ps[j].x, ps[j].y);
            ctx.strokeStyle = 'rgba(54, 100, 209, ' + a + ')';
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }

      for (const p of ps) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha;
        ctx.fill();
        ctx.globalAlpha = 1;
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > cw) p.vx *= -1;
        if (p.y < 0 || p.y > ch) p.vy *= -1;
      }

      animFrameRef.current = requestAnimationFrame(draw);
    };

    resize();
    createParticles();
    draw();

    const handleResize = () => { resize(); createParticles(); };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  useEffect(() => {
    const cleanup = initCanvas();
    return () => { if (cleanup) cleanup(); };
  }, [initCanvas]);

  const channelKeys = ['line', 'fbPosts', 'messenger', 'instagram', 'webChat', 'phone'];

  return (
    <Box component="section" sx={{
      pt: { xs: '120px', md: '140px' },
      pb: { xs: '60px', md: '100px' },
      background: 'linear-gradient(180deg, #f0f4f9 0%, #e8eef6 60%, #dde5f0 100%)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Particle canvas */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }}>
        <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
      </Box>

      {/* Decorative gradients */}
      <Box sx={{ position: 'absolute', width: 700, height: 700, top: -200, right: -150, background: 'radial-gradient(circle, rgba(54,100,209,0.08) 0%, transparent 70%)', borderRadius: '50%' }} />
      <Box sx={{ position: 'absolute', width: 500, height: 500, bottom: -200, left: -100, background: 'radial-gradient(circle, rgba(6,199,85,0.05) 0%, transparent 70%)', borderRadius: '50%' }} />

      {/* Floating badges */}
      {BADGE_POSITIONS.map((badge, i) => (
        <Box key={i} className={`omni-float-badge ${badge.className}`}>
          <Box className="omni-float-icon" sx={{ background: badge.bgColor }}>
            <svg width="16" height="16" fill="#fff" viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
          </Box>
          <span>{t(`hero.floatBadge${i + 1}`)}</span>
        </Box>
      ))}

      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center', maxWidth: 1440, mx: 'auto', px: { xs: '20px', md: '40px' } }}>
        <Box sx={{ maxWidth: 760, mx: 'auto' }}>
          {/* Eyebrow */}
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            px: '18px', py: '6px',
            background: 'rgba(54,100,209,0.08)',
            border: '1px solid rgba(54,100,209,0.12)',
            borderRadius: '50px',
            fontSize: 16, fontWeight: 600, color: '#3664D1',
            mb: '28px',
          }}>
            <Box sx={{ width: 8, height: 8, background: '#3664D1', borderRadius: '50%', animation: 'omni-pulse-glow 2s infinite' }} />
            {t('hero.eyebrow')}
          </Box>

          {/* Title */}
          <Typography variant="h1" sx={{
            fontSize: { xs: '2.2rem', md: 'clamp(2.6rem, 5.5vw, 4rem)' },
            fontWeight: 900, lineHeight: 1.15, letterSpacing: '-1px', mb: 3,
            color: '#0f1729', wordBreak: 'keep-all', overflowWrap: 'anywhere',
          }}>
            {t('hero.title').split('\n').map((line, i) => (
              <span key={i}>
                {i === 0 ? (
                  <Box component="span" sx={{ background: 'linear-gradient(135deg, #3664D1, #5b8def)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                    {line}
                  </Box>
                ) : line}
                {i === 0 && <br />}
              </span>
            ))}
          </Typography>

          {/* Subtitle */}
          <Typography sx={{
            fontSize: { xs: 18, md: 21 }, color: '#374151', lineHeight: 1.8,
            mb: '40px', maxWidth: 720, mx: 'auto', whiteSpace: 'pre-line',
          }}>
            {t('hero.subtitle')}
          </Typography>

          {/* CTA Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '48px' }}>
            <Button
              variant="contained"
              onClick={onCtaClick}
              sx={{
                px: '36px', py: '14px', fontSize: 18, fontWeight: 600,
                borderRadius: '8px', background: '#3664D1', textTransform: 'none',
                boxShadow: '0 4px 14px rgba(54,100,209,0.3)',
                '&:hover': { background: '#2850b8', transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(54,100,209,0.35)' },
              }}
            >
              {t('hero.ctaPrimary')}
            </Button>
          </Box>

          {/* Channel Pills with colored dots */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {channelKeys.map((key) => (
              <Box key={key} className="omni-channel-pill">
                <span className="dot" style={{ background: CHANNEL_COLORS[key] }} />
                {t(`channels.${key}`)}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
